'use client';

import { useEffect, useRef } from 'react';

/** Long enough to read as the wave settling, short enough not to trail the cursor. */
const SETTLE_MS = 450;

/**
 * The way out of the logo's hover wave.
 *
 * The wave itself is CSS (see `styles/components/logo.css`); only leaving it
 * needs script. Dropping the animation puts every triangle back to full
 * opacity in a single frame, and a CSS transition will not catch that: the
 * value the triangle is leaving was never in its own computed style, only in
 * the animation's output, so the browser sees no change to interpolate. That
 * was measured, not assumed.
 *
 * So the last rendered opacity is read while the wave is still running, and
 * the ride back up to full is played from there. Two details make it work:
 * the wave hangs off `.is-hovering` rather than `:hover`, because by the time
 * `pointerleave` fires `:hover` is already gone and every triangle would
 * report a flat 1; and the fade is started before the class comes off, so no
 * frame is drawn at full opacity in between.
 */
export function useLogoWave<T extends HTMLElement>() {
    const ref = useRef<T>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        let settling: Animation[] = [];
        const triangles = () => el.querySelectorAll<SVGElement>('.logo__tri');
        const cancel = () => {
            settling.forEach((animation) => animation.cancel());
            settling = [];
        };

        // Asked per event rather than once on mount, so switching to a mouse or
        // turning motion back down takes effect without a reload. The CSS asks
        // the same two questions, and the two must not disagree.
        const wanted = () =>
            window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const onEnter = () => {
            if (!wanted()) return;
            cancel();
            el.classList.add('is-hovering');
        };

        const onLeave = () => {
            if (!el.classList.contains('is-hovering')) return;

            const frozen = [...triangles()].map((tri) => [tri, getComputedStyle(tri).opacity] as const);
            cancel();
            settling = frozen.map(([tri, from]) =>
                tri.animate([{ opacity: from }, { opacity: 1 }], { duration: SETTLE_MS, easing: 'ease-out' }),
            );
            el.classList.remove('is-hovering');
        };

        el.addEventListener('pointerenter', onEnter);
        el.addEventListener('pointerleave', onLeave);

        return () => {
            el.removeEventListener('pointerenter', onEnter);
            el.removeEventListener('pointerleave', onLeave);
            el.classList.remove('is-hovering');
            cancel();
        };
    }, []);

    return ref;
}
